import type { ReactNode } from 'react';
import { Body1Strong, Caption1 } from '@fluentui/react-components';

import { AppLink, ControlledTooltip, CustomScrollbar } from '@shared/components';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { buildHref } from '@shared/utils';
import splitExtension from '@modules/items/helpers/splitExtension.helper';

import { GROUNDED_QA_NO_SOURCES_CAPTION, GROUNDED_QA_UNKNOWN_SOURCE_LABEL } from '../groundedQa/constants';
import { sourceTypeToPath } from '../groundedQa/helpers';
import SourceIcon from '../groundedQa/components/SourceIcon';
import { EAiCitationKind, EAiCitationSourceType, type TAiCitationResponse } from '../types';

const SOURCE_LABEL_TOOLTIP_THRESHOLD = 50;

// Clicking the same source again keeps every parameter, so the page can only tell it happened by
// the navigation itself - and a query-param write elsewhere must not look the same.
const SCROLL_REQUEST_STATE = { scrollRequest: true };

type TProps = {
  citationSources: TAiCitationResponse[];
  // The origin document the answer is about; citations pointing at it open in place, not a new tab.
  item?: string;
  runId?: string;
  headerAccessory?: ReactNode;
};

const labelFor = (source: TAiCitationResponse): string => {
  const name = source.sourceName ?? GROUNDED_QA_UNKNOWN_SOURCE_LABEL;
  // A paper title is not a filename: stripping after the last dot cuts "Comparison of A vs. B".
  return source.sourceType === EAiCitationSourceType.LITERATURE ? name : splitExtension(name)[0];
};

const isCurrentDocumentSource = (source: TAiCitationResponse, item: string | undefined): boolean =>
  source.sourceType === EAiCitationSourceType.DOCUMENT && source.source === item;

const isCurrentDocumentMetadataCitation = (source: TAiCitationResponse, item: string | undefined): boolean =>
  isCurrentDocumentSource(source, item) && source.kind === EAiCitationKind.ITEM_METADATA;

// A citation to the document being edited opens in place, and the document leaves edit mode as soon
// as the view stops saying 'edit'.
const hrefFor = (
  source: TAiCitationResponse,
  index: number,
  runId: string | undefined,
  view: 'edit' | 'latest'
): string => {
  const isDocument = source.sourceType === EAiCitationSourceType.DOCUMENT;
  return buildHref(`/${sourceTypeToPath(source.sourceType)}/${source.source}`, {
    view,
    ...(isDocument && source.kind === EAiCitationKind.KNOWLEDGE_CHUNK && runId
      ? { qaRun: runId, qaCitation: String(index) }
      : isDocument && source.kind === EAiCitationKind.KNOWLEDGE_CHUNK
        ? { elem: source.elementId, highlightText: source.quote }
        : {}),
  });
};

const AiAssistantSources = ({ citationSources, item, runId, headerAccessory }: TProps) => {
  const { searchParams } = useSearchParams();
  const isEditing = searchParams.get('view') === 'edit';
  const hasSources = citationSources.length > 0;
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-2 pt-5 pb-3 shrink-0 flex items-center justify-between gap-2">
        <Body1Strong>Sources</Body1Strong>
        {headerAccessory}
      </div>
      {hasSources ? (
        <CustomScrollbar className="w-full overflow-x-hidden flex-1 min-h-0">
          <ul className="flex flex-col gap-3 px-2 pb-4">
            {citationSources.map((source, index) => {
              const label = labelFor(source);
              const isCurrentSource = isCurrentDocumentSource(source, item);
              const isCurrentDocumentMetadata = isCurrentDocumentMetadataCitation(source, item);
              const quote = source.kind === EAiCitationKind.KNOWLEDGE_CHUNK ? source.quote : undefined;
              const metadataLabels = source.kind === EAiCitationKind.ITEM_METADATA ? source.metadataLabels : undefined;
              const metadataText = metadataLabels?.join(', ') ?? '';
              const hasMetadataLabels = Boolean(metadataLabels?.length);
              const labelNode = (
                <ControlledTooltip
                  enable={label.length > SOURCE_LABEL_TOOLTIP_THRESHOLD}
                  content={label}
                  relationship="label"
                  positioning="before"
                  withArrow
                >
                  <span className={`!block !w-full !truncate${isCurrentDocumentMetadata ? ' text-neutralFg-1' : ''}`}>
                    {label}
                  </span>
                </ControlledTooltip>
              );
              return (
                <li key={`${source.source}-${index}`} className="flex items-start gap-2 min-w-0">
                  <span className="text-neutralFg-3 shrink-0 inline-block min-w-7">[{index + 1}]</span>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="shrink-0 inline-flex items-center h-5">
                        <SourceIcon sourceType={source.sourceType} />
                      </span>
                      {isCurrentDocumentMetadata ? (
                        labelNode
                      ) : (
                        <AppLink
                          to={hrefFor(source, index, runId, isCurrentSource && isEditing ? 'edit' : 'latest')}
                          state={SCROLL_REQUEST_STATE}
                          {...(isCurrentSource ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                          className="min-w-0 flex-1"
                        >
                          {labelNode}
                        </AppLink>
                      )}
                    </div>
                    {quote && (
                      <ControlledTooltip
                        enable={quote.length > SOURCE_LABEL_TOOLTIP_THRESHOLD}
                        content={quote}
                        relationship="label"
                        positioning="before"
                        withArrow
                      >
                        <Caption1 className="text-neutralFg-3 italic !block !w-full !truncate">{`"${quote}"`}</Caption1>
                      </ControlledTooltip>
                    )}
                    {hasMetadataLabels && (
                      <ControlledTooltip
                        enable={metadataText.length > SOURCE_LABEL_TOOLTIP_THRESHOLD}
                        content={metadataText}
                        relationship="label"
                        positioning="before"
                        withArrow
                      >
                        <Caption1 className="text-neutralFg-3 !block !w-full">
                          <span className="!block !w-full !truncate">
                            <span className="font-semibold">Metadata used:</span>{' '}
                            <span className="capitalize">{metadataText}</span>
                          </span>
                        </Caption1>
                      </ControlledTooltip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </CustomScrollbar>
      ) : (
        <Caption1 className="text-neutralFg-3 px-2 pb-4">{GROUNDED_QA_NO_SOURCES_CAPTION}</Caption1>
      )}
    </div>
  );
};

export default AiAssistantSources;
